const PREFIX = "phaco-usage-v1:";
const MAX_AGE = 7 * 86400000;
const MAX_INTERVALS = 2000;
const BATCH_SIZE = 20;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// One immutable storage key per interval: another tab can replay/delete that
// exact interval without overwriting newly queued events. Concurrent replay is
// harmless because the server deduplicates IDs transactionally.
export function createUsageQueue({ uid, storage, send, isCurrentUser, onLoss, now = Date.now }) {
  const prefix = `${PREFIX}${encodeURIComponent(uid)}:`;
  const memory = new Map();
  let draining = false;
  let stopped = false;
  let retryAfter = 0;
  let failures = 0;
  const remove = id => {
    memory.delete(id);
    try { storage?.removeItem(`${prefix}${id}`); } catch (_) { /* A later replay is deduplicated. */ }
  };
  function pending() {
    const events = new Map(memory);
    try {
      const keys = [];
      for (let index = 0; index < storage.length; index++) {
        const name = storage.key(index);
        if (name?.startsWith(prefix)) keys.push(name);
      }
      for (const name of keys) {
        const id = name.slice(prefix.length);
        try {
          const event = JSON.parse(storage.getItem(name));
          if (event) events.set(id, event);
        } catch (_) { events.set(id, null); }
      }
    } catch (_) { /* The in-memory queue works when storage is unavailable. */ }
    let dropped = 0;
    for (const [id, event] of events) {
      if (!event || !UUID.test(id) || event.eventId !== id || !Number.isFinite(event.endedAtMs) || event.endedAtMs < now() - MAX_AGE) {
        remove(id); events.delete(id); dropped++;
      }
    }
    const ordered = [...events.values()].sort((a, b) => a.endedAtMs - b.endedAtMs);
    const excess = Math.max(0, ordered.length - MAX_INTERVALS);
    for (const event of ordered.slice(0, excess)) remove(event.eventId);
    if (dropped + excess) onLoss(dropped + excess);
    return ordered.slice(excess);
  }
  function push(interval) {
    memory.set(interval.eventId, interval);
    try { storage?.setItem(`${prefix}${interval.eventId}`, JSON.stringify(interval)); } catch (_) { /* Retry from memory. */ }
    pending();
  }
  async function drain() {
    if (stopped || draining || !isCurrentUser() || now() < retryAfter) return;
    draining = true;
    try {
      const events = pending();
      // Bound a replay pass to 200 intervals; subsequent ticks drain the rest.
      for (let offset = 0; offset < Math.min(events.length, 200) && !stopped && isCurrentUser(); offset += BATCH_SIZE) {
        const batch = events.slice(offset, offset + BATCH_SIZE);
        const result = await send(batch);
        const acknowledged = new Set([...(result.accepted || []), ...(result.duplicates || []), ...(result.rejected || []).map(item => item.eventId)]);
        if (!batch.some(event => acknowledged.has(event.eventId))) throw new Error("Usage batch not acknowledged");
        for (const event of batch) if (acknowledged.has(event.eventId)) remove(event.eventId);
        if (result.rejected?.length) onLoss(result.rejected.length);
      }
      failures = 0;
      retryAfter = 0;
    } catch (_) {
      failures += 1;
      retryAfter = now() + Math.min(300000, 5000 * 2 ** Math.min(failures, 6));
    } finally { draining = false; }
  }
  return { push, drain, start() { stopped = false; }, stop() { stopped = true; }, pending: () => pending().length };
}
